import React from 'react';
import { useSpring, animated, config } from 'react-spring';
import SEO from '../../components/SEO';

const CookiePolicy = () => {
    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]";
    const secondaryTextColor = "text-[#212529]/80";
    const tertiaryTextColor = "text-[#212529]/60";

    // Animation for content entrance
    const contentAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
        config: { ...config.gentle, tension: 120, friction: 14 },
        delay: 100
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO
                title="Local Storage Policy - Axialis"
                description="Local storage and cookie policy for the Axialis website."
                path="/cookies"
            />
            <div className="container mx-auto px-4 py-32">
                <animated.div style={contentAnimation} className="max-w-4xl mx-auto backdrop-blur-sm bg-white/40 p-8 md:p-12 rounded-xl shadow-xl border border-white/40">
                    <h1 className={`text-4xl font-display font-bold mb-6 ${primaryTextColor}`}>Local Storage Policy</h1>
                    <div className={`h-0.5 w-16 bg-primary mb-8`}></div>
                    <p className={`${secondaryTextColor} mb-8`}>Last Updated: {new Date().toLocaleDateString()}</p>

                    <div className="space-y-8">
                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Introduction</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>This policy explains our simple approach to data storage. We do not use cookies for tracking, analytics, or advertising purposes.</p>
                                <p>The only data stored in your browser is for remembering your preferences to improve your experience on our website. No consent banner is needed because we don't track you.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>What We Store Locally</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>We only use your browser's local storage for a single purpose:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>Intro Animation Status:</span> A simple flag that remembers whether you have already seen the website's intro animation, so it is not shown again on repeat visits.</li>
                                </ul>
                                <p>This data never leaves your device and is not shared with anyone. It contains no personal information whatsoever.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>What We Don't Use</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>To be completely transparent, we do NOT use:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Tracking cookies</li>
                                    <li>Analytics cookies</li>
                                    <li>Advertising cookies</li>
                                    <li>Third-party cookies</li>
                                    <li>Session cookies for user identification</li>
                                    <li>Any form of cross-site tracking</li>
                                    <li>Google Analytics or similar services</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Your Control</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>You have complete control over this local storage data. You can:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Clear all local storage data through your browser settings</li>
                                    <li>Use private/incognito mode to prevent any local storage</li>
                                    <li>Disable local storage entirely in your browser (though some preferences won't be remembered)</li>
                                </ul>
                                <p>Since this data never leaves your device and we don't track you, there's no need for consent banners or complex privacy controls.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>How to Clear Local Storage</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>If you want to clear your stored preferences, you can do so in your browser:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>Chrome:</span> Settings → Privacy and security → Clear browsing data → Cookies and other site data</li>
                                    <li><span className={primaryTextColor}>Firefox:</span> Settings → Privacy & Security → Cookies and Site Data → Clear Data</li>
                                    <li><span className={primaryTextColor}>Safari:</span> Settings → Privacy → Manage Website Data → Remove All</li>
                                    <li><span className={primaryTextColor}>Edge:</span> Settings → Privacy, search, and services → Clear browsing data</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>Contact Us</h2>
                            <div className={`${secondaryTextColor} space-y-4 leading-relaxed`}>
                                <p>If you have any questions about our local storage practices, please contact us:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>By email: <a href="mailto:support@rivieratech.solutions" className="text-primary hover:underline">support@rivieratech.solutions</a></li>
                                    <li>Through the contact page on our website</li>
                                </ul>
                            </div>
                        </section>

                        {/* Attribution backlink */}
                        <div className="pt-8 mt-8 border-t border-[#212529]/10">
                            <p className="text-[#212529]/40 text-xs text-center">
                                This website was designed and built by{' '}
                                <a
                                    href="https://rivieratech.solutions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary/60 hover:text-primary hover:underline transition-colors duration-300"
                                >
                                    RivieraTech Solutions
                                </a>
                                {' '}&mdash; a professional website builder specializing in business websites, software development, hardware design, and electronics consulting.
                            </p>
                        </div>
                    </div>
                </animated.div>
            </div>
        </div>
    );
};

export default CookiePolicy;
